import { useEffect, useRef } from 'react';

export interface LevelSource {
  /** 最新の音量。React state ではなく ref 経由で読む。 */
  current: { rms: number; tooQuiet: boolean; clipping: boolean };
}

/**
 * 入力音量メーター。
 *
 * 音量は毎秒20回以上更新される。これを React state に載せると
 * 字幕リスト全体が再描画され続けて実用にならないため、
 * ref から requestAnimationFrame で直接 DOM を触る。
 */
export function LevelMeter({ source }: { source: LevelSource }): JSX.Element {
  const barRef = useRef<HTMLSpanElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame = 0;

    const tick = (): void => {
      const bar = barRef.current;
      const wrap = wrapRef.current;
      if (bar && wrap) {
        const { rms, tooQuiet, clipping } = source.current;
        // rms は 0〜1 だが実用域は 0〜0.3 程度。見やすいよう拡大する。
        const percent = Math.min(100, Math.round(rms * 300));
        bar.style.width = `${percent}%`;
        wrap.classList.toggle('quiet', tooQuiet);
        wrap.classList.toggle('clipping', clipping);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [source]);

  return (
    <div className="meter" ref={wrapRef} aria-label="入力音量">
      <span ref={barRef} style={{ width: '0%' }} />
    </div>
  );
}
